'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: 'process' | 'timing' | 'cost' | 'commitment';
}

interface ProcessFAQProps {
  isHydrated: boolean;
}

export default function ProcessFAQ({ isHydrated }: ProcessFAQProps) {
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const faqItems: FAQItem[] = [
    {
      id: 1,
      question: 'Jak dlouho trvá celý proces?',
      answer: 'Záleží na zvolené cestě a vaší situaci. Expresní cesta trvá 2-3 týdny, standardní 4-6 týdnů a komplexní 2-3 měsíce. Vždy postupujeme vaším tempem a můžeme proces přizpůsobit vašim časovým možnostem.',
      category: 'timing',
    },
    {
      id: 2,
      question: 'Musím se zavázat k dlouhodobé spolupráci?',
      answer: 'Ne, nejste vázáni žádnou dlouhodobou smlouvou. Můžete začít s jednou konzultací nebo krátkodobým projektem. Dlouhodobá spolupráce je nabízena těm, kteří chtějí průběžnou podporu, ale není povinná.',
      category: 'commitment',
    },
    {
      id: 3,
      question: 'Co když si nejsem jistý, kterou cestu zvolit?',
      answer: 'To je naprosto v pořádku! Během online diagnózy a první schůzky společně zjistíme, jaká cesta je pro vás nejvhodnější. Můžete také začít s expresní cestou a později přejít na komplexnější variantu.',
      category: 'process',
    },
    {
      id: 4,
      question: 'Kolik to bude stát?',
      answer: 'Cena závisí na zvolené cestě a rozsahu služeb. Online diagnóza je zdarma. První konzultace stojí 2 500 Kč, kterou můžete odečíst při pokračování spolupráce. Detailní ceník vám poskytneme po první schůzce na základě vašich potřeb.',
      category: 'cost',
    },
    {
      id: 5,
      question: 'Můžu proces kdykoliv pozastavit?',
      answer: 'Ano, proces můžete kdykoliv pozastavit nebo ukončit. Platíte pouze za služby, které jste skutečně využili. Pokud se rozhodnete pokračovat později, můžeme navázat tam, kde jsme skončili.',
      category: 'commitment',
    },
    {
      id: 6,
      question: 'Co když se moje situace během procesu změní?',
      answer: 'To je běžné a počítáme s tím. Váš finanční plán je živý dokument, který průběžně upravujeme podle změn ve vašem životě. Změny můžeme zapracovat kdykoliv během procesu.',
      category: 'process',
    },
    {
      id: 7,
      question: 'Jak probíhají schůzky - online nebo osobně?',
      answer: 'Nabízíme obě možnosti. Můžete si vybrat osobní schůzky v naší kanceláři v Praze-Chodově nebo online konzultace přes video hovor. Kvalita služeb je stejná v obou případech.',
      category: 'process',
    },
    {
      id: 8,
      question: 'Co když budu potřebovat pomoc mezi schůzkami?',
      answer: 'Jsme tu pro vás i mezi plánovanými schůzkami. Můžete nás kontaktovat emailem nebo telefonem s dotazy. U komplexní cesty máte navíc prioritní podporu s rychlejší odezvou.',
      category: 'timing',
    },
  ];

  const categories = [
    { id: 'all', label: 'Vše', icon: 'Squares2X2Icon' },
    { id: 'process', label: 'Proces', icon: 'ArrowPathIcon' },
    { id: 'timing', label: 'Časování', icon: 'ClockIcon' },
    { id: 'cost', label: 'Cena', icon: 'CurrencyDollarIcon' },
    { id: 'commitment', label: 'Závazky', icon: 'HandRaisedIcon' },
  ];

  const filteredFAQs = activeCategory === 'all' 
    ? faqItems 
    : faqItems.filter(item => item.category === activeCategory);

  const handleFAQClick = (id: number) => {
    if (!isHydrated) return;
    setOpenFAQ(openFAQ === id ? null : id);
  };

  const handleCategoryChange = (category: string) => {
    if (!isHydrated) return;
    setActiveCategory(category);
    setOpenFAQ(null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-2xl lg:text-4xl font-bold text-primary mb-4 text-center font-headline">
        Často kladené otázky
      </h2>
      <p className="text-lg text-text-secondary text-center mb-8">
        Odpovědi na nejčastější dotazy o našem procesu
      </p>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 justify-center mb-8">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => handleCategoryChange(category.id)}
            className={`px-4 py-2 rounded-lg font-medium transition-smooth flex items-center space-x-2 ${
              isHydrated && activeCategory === category.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-surface text-text-primary hover:bg-primary/10'
            }`}
          >
            <Icon name={category.icon as any} size={16} className="text-current" />
            <span>{category.label}</span>
          </button>
        ))}
      </div>

      {/* FAQ Items */}
      <div className="space-y-4">
        {filteredFAQs.map((item) => (
          <div
            key={item.id}
            className="bg-card rounded-xl shadow-card overflow-hidden transition-smooth hover:shadow-elevated"
          >
            <button
              onClick={() => handleFAQClick(item.id)}
              className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-surface transition-smooth"
            >
              <span className="text-lg font-semibold text-primary pr-4">{item.question}</span>
              <Icon
                name="ChevronDownIcon"
                size={20}
                className={`text-primary flex-shrink-0 transition-transform ${
                  isHydrated && openFAQ === item.id ? 'rotate-180' : ''
                }`}
              />
            </button>
            {isHydrated && openFAQ === item.id && (
              <div className="px-6 pb-4">
                <p className="text-text-secondary">{item.answer}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Contact CTA */}
      <div className="mt-8 p-6 bg-trust rounded-xl border border-border text-center">
        <p className="text-text-secondary mb-4">
          Nenašli jste odpověď na svou otázku?
        </p>
        <a
          href="mailto:info@co-work.cz"
          className="inline-flex items-center space-x-2 text-primary font-semibold hover:text-secondary transition-smooth"
        >
          <Icon name="EnvelopeIcon" size={20} className="text-current" />
          <span>Kontaktujte nás</span>
        </a>
      </div>
    </div>
  );
}